import clsx from 'clsx';
import moment from 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { TimezoneModal } from '@/core/TimezoneModal.molecule';
import { sqWorkbenchStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import utilities from '@/core/utilities';
import { DateTimePicker } from '@/datetime/DateTimePicker.atom';
import { PARSE_TYPE, TIME_FORMAT_NUMERICAL_SHORT, parseDatetimeInput } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import { sqTimezones } from '@/utilities/datetime.constants';
import { successToast } from '@/utilities/toast.utilities';
import { Button, ButtonWithPopover } from '@seeqdev/qomponents';
import type { Timezone } from '@/datetime/timezone.service';

/**
 * Entry and display of date/times by text and with date picker, including time zones
 *
 * Relies on `DateTimePicker` for the date/time selection UI, which uses `flatpickr`
 *
 *    User Input → DateTimeEntry → DateTimePicker → Flatpickr
 *      ↓                ↓              ↓                ↓
 *    Parse/Validate → Update State → Format Display → DOM Update
 */
export interface DateTimeEntryProps {
  testId: string;
  date: moment.Moment | undefined;
  otherDate?: moment.Moment;
  updateDate: (date: moment.Moment | undefined) => void;
  updateBothDates?: (start: any, end: any, option?: string) => void;
  timezone: Timezone;
  readOnly?: boolean;
  timeZoneReadOnly?: boolean;
  hideTimezone?: boolean;
  dateFormat?: string;
  trackCategory?: string;
  trackInformation?: string;
  trackAction?: string;
  extraClassName?: string;
  datePickerExtraClassNames?: string;
  inputHeight?: number;
  showSeconds?: boolean;
  isInsideModal?: boolean;
  // this property is used by FormBuilder to indicate there's an error
  showError?: boolean;
  /**
   * The minimum date that a user can start picking from (inclusive).
   */
  minDate?: moment.Moment;
  /**
   * The maximum date that a user can pick to (inclusive).
   */
  maxDate?: moment.Moment;
  /**
   * Adjusts the step for the minute input
   */
  minuteIncrement?: number;
  /**
   * Flag to control whether the date picker is open. If undefined, the date picker will be open when the
   */
  isPickerOpen?: boolean;
  /**
   * Show a button to copy the date and time to the clipboard
   */
  copyButtonLocation?: CopyButtonLocation;
  /**
   * Flag to control whether the date should be dynamically updated when only month/year changes in the picker.
   * When false, clicking "Done" will not auto-update the selected date to match the current month/year.
   * Defaults to true.
   */
  autoUpdateDate?: boolean;
}

export enum CopyButtonLocation {
  Left,
  Right,
  Off,
}

export const DateTimeEntry = ({
  testId,
  date,
  otherDate,
  updateDate,
  updateBothDates,
  timezone,
  readOnly = false,
  timeZoneReadOnly,
  hideTimezone = false,
  dateFormat = TIME_FORMAT_NUMERICAL_SHORT,
  trackAction = 'DateTimeEntry',
  trackCategory = 'Date selected',
  trackInformation,
  extraClassName = '',
  datePickerExtraClassNames = '',
  inputHeight = 20,
  showSeconds = true,
  isInsideModal = false,
  showError = false,
  minDate,
  maxDate,
  minuteIncrement,
  isPickerOpen,
  copyButtonLocation = CopyButtonLocation.Off,
  autoUpdateDate,
}: DateTimeEntryProps) => {
  const { t } = useTranslation();

  const { use24HourFormat, dateFormat: dateFormatPreference } = useFluxPaths(sqWorkbenchStore, [
    'use24HourFormat',
    'dateFormat',
  ]);
  const effectiveDateFormat = getPreferredTimeFormat(use24HourFormat, dateFormat);

  const [datetimeText, setDatetimeText] = useState('');
  const [tzAbbreviation, setTzAbbreviation] = useState('');
  const [isError, setIsError] = useState(false);
  const [parseError, setParseError] = useState<string | undefined>(undefined);
  const [showTimezoneModal, setShowTimezoneModal] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [mouseEntered, setMouseEntered] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_TEXT_LENGTH = 50;
  const dateTimestamp = date?.valueOf();

  const copyDateWithFormat = useCallback(
    async (event: React.MouseEvent | React.KeyboardEvent) => {
      let dateText = '';
      let messageKey = 'COPY_DATETIME_TO_CLIPBOARD';
      if (event.shiftKey) {
        dateText = date?.toISOString() || '';
        messageKey = 'COPY_DATETIME_ISO_TO_CLIPBOARD';
      } else {
        dateText = date?.tz(timezone.name).format(effectiveDateFormat) || '';
        messageKey = 'COPY_DATETIME_TO_CLIPBOARD';
      }
      await utilities.copyTextToClipboard(dateText);
      successToast({ messageKey });
    },
    [date, effectiveDateFormat, timezone],
  );

  const showCopyButton = hasFocus || mouseEntered;

  let copyButton = (
    <Button
      onClick={copyDateWithFormat}
      extraClassNames={`copyDatetimeIcon ${showCopyButton ? 'tw:visible' : 'tw:hidden'}`}
      variant="no-border"
      size="xs"
      tooltip={t('DISPLAY_RANGE_DATE_TIME_COPY')}
      tooltipOptions={{ delay: 1000, position: 'bottom' }}
      icon="fa-copy"
    />
  );

  const updateDisplayValue = useCallback(() => {
    if (date && date.isValid && date.isValid() && timezone) {
      setDatetimeText(date.tz(timezone.name).format(effectiveDateFormat) || '');
      setTzAbbreviation(sqTimezones.getAbbreviatedDisplay(timezone, date));
    } else {
      setDatetimeText('');
      setTzAbbreviation('');
    }
  }, [date, timezone, effectiveDateFormat]);

  useEffect(() => {
    updateDisplayValue();
  }, [timezone, dateTimestamp, updateDisplayValue, effectiveDateFormat, dateFormatPreference]);

  const isManualInputValid = (newDate: string) => newDate.length < MAX_TEXT_LENGTH;

  const parseManualDatetimeChange = (newDate: string) => {
    if (!isManualInputValid(newDate)) {
      showParsingError(`${newDate.substring(0, 40)}...`);
      return;
    }

    // If date is undefined, use current moment as fallback for parsing context
    const fallbackDate = date || moment();

    const parseResult = parseDatetimeInput(newDate, {
      date: fallbackDate,
      otherDate,
      timezone,
      now: moment.utc(),
      updatingTwoDates: !!updateBothDates,
    });
    if (!parseResult) {
      showParsingError(newDate.trim());
      return;
    }

    clearError();

    switch (parseResult.type) {
      case PARSE_TYPE.SINGLE:
        updateDate(parseResult.date.clone());
        break;
      case PARSE_TYPE.DOUBLE:
        updateBothDates?.(parseResult.start, parseResult.end);
        break;
    }

    return parseResult.date;
  };

  const clearError = () => {
    setIsError(false);
    setParseError(undefined);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const showParsingError = (input: string) => {
    updateDisplayValue();
    setIsError(true);
    setParseError(input);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(clearError, 3000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const errorTooltip = (
    <span>
      {t('PARSE_DATE_ERROR')}
      <span className="text-bolder">{parseError}</span>
    </span>
  );

  return (
    <div
      className={clsx('nowrap dateTimeEntry', extraClassName)}
      onMouseEnter={() => setMouseEntered(true)}
      onFocus={() => setHasFocus(true)}
      onMouseLeave={() => setMouseEntered(false)}
      onBlur={() => setHasFocus(false)}>
      {readOnly && <span className="specDateTime readOnly sq-fairly-dark-gray">{datetimeText}</span>}
      {copyButtonLocation === CopyButtonLocation.Left && copyButton}
      {!readOnly && (
        <ButtonWithPopover
          isOpen={isError}
          extraPopoverClassNames="errorTooltip tooltip-inner"
          trigger={
            <DateTimePicker
              key={`${use24HourFormat ? '24h' : '12h'}-${dateFormatPreference}`}
              showError={showError}
              value={date}
              testId={testId}
              localizedDateFormat={effectiveDateFormat}
              timezone={timezone}
              onDatetimeSelect={updateDate}
              onManualDatetimeInput={parseManualDatetimeChange}
              trackCategory={trackCategory}
              trackAction={trackAction}
              trackInformation={{ parentInfo: trackInformation }}
              extraClassNames={clsx('text-center', 'fs12', 'p0', datePickerExtraClassNames)}
              inputHeight={inputHeight}
              enableSeconds={showSeconds}
              isInsideModal={isInsideModal}
              minDate={minDate}
              maxDate={maxDate}
              minuteIncrement={minuteIncrement}
              isOpen={isPickerOpen}
              autoUpdateDate={autoUpdateDate}
            />
          }>
          {errorTooltip}
        </ButtonWithPopover>
      )}
      {!hideTimezone && (readOnly || timeZoneReadOnly) && (
        <span className="specTimezone readOnly pl5">{tzAbbreviation}</span>
      )}
      {!hideTimezone && !readOnly && !timeZoneReadOnly && (
        <FakeLink testId="openTimezoneModal" extraClassNames="pl5" onClick={() => setShowTimezoneModal(true)}>
          {tzAbbreviation}
        </FakeLink>
      )}
      {showTimezoneModal && <TimezoneModal close={() => setShowTimezoneModal(false)} userFallback={true} />}
      {copyButtonLocation === CopyButtonLocation.Right && copyButton}
    </div>
  );
};
